import { LoggerInterface } from "@interfaces/LoggerInterface";

import { DynamicModuleDefinition, Tag } from "./types";

export class DynamicImportManager {
    private readonly registeredModules = new Set<DynamicModuleDefinition>();
    private readonly tags = new Map<Tag, DynamicModuleDefinition[]>();
    private readonly deferredResolves = new Map<
        DynamicModuleDefinition,
        Array<() => void>
    >();
    private readonly fetchedModules = new Set<DynamicModuleDefinition>();
    private readonly logger: LoggerInterface;

    constructor(logger: LoggerInterface) {
        this.logger = logger.newLogger("DynamicImportManager");
        this.logger.debug("DynamicImportManager initialized.");
    }

    private resolveDeferred<T>(definition: DynamicModuleDefinition<T>): void {
        const resolves = this.deferredResolves.get(definition);

        if (resolves) {
            resolves.forEach((resolve) => resolve && resolve());
            this.deferredResolves.delete(definition);
        }
    }

    private async loadModule<T>(
        definition: DynamicModuleDefinition<T>
    ): Promise<T> {
        if (!this.registeredModules.has(definition)) {
            throw new Error(`Module '${definition.name}' is not registered.`);
        }

        try {
            const importedModule = await definition.importCallback();
            this.fetchedModules.add(definition);
            this.resolveDeferred(definition);
            return importedModule;
        } catch (error) {
            this.logger.error(
                `Failed to load module '${definition.name}': ${String(error)}`
            );
            throw error;
        }
    }

    public register(definition: DynamicModuleDefinition, tags?: Tag[]): void {
        if (this.registeredModules.has(definition)) {
            throw new Error(
                `Module '${definition.name}' is already registered.`
            );
        }

        this.registeredModules.add(definition);

        tags?.forEach((tag) => {
            const definitions = this.tags.get(tag) || [];
            definitions.push(definition);
            this.tags.set(tag, definitions);
        });
    }

    public async fetch(identifier: {
        definition?: DynamicModuleDefinition;
        tag?: Tag;
    }): Promise<void> {
        const { definition, tag } = identifier;

        if (!definition && !tag) {
            throw new Error(
                "Identifier must have either a module definition or tag."
            );
        }

        if (definition && !this.fetchedModules.has(definition)) {
            await this.loadModule(definition);
        }

        if (tag) {
            const definitions = this.tags.get(tag);

            if (!definitions) {
                throw new Error(`Tag '${String(tag)}' is not registered.`);
            }

            await Promise.allSettled(
                definitions
                    .filter((definition) => {
                        return !this.fetchedModules.has(definition);
                    })
                    .map((definition) => {
                        return this.loadModule(definition);
                    })
            );
        }
    }

    public async import<T>(
        definition: DynamicModuleDefinition<T>,
        immediate = false
    ): Promise<T> {
        if (!this.registeredModules.has(definition)) {
            throw new Error(`Module '${definition.name}' is not registered.`);
        }

        if (immediate || this.fetchedModules.has(definition)) {
            return await this.loadModule(definition);
        }

        return new Promise<T>((resolve) => {
            const resolves = this.deferredResolves.get(definition) || [];
            resolves.push(resolve as () => void);
            this.deferredResolves.set(definition, resolves);
        }).then(() => {
            return this.loadModule(definition);
        });
    }

    public async importDefault<T>(
        definition: DynamicModuleDefinition<{ default: T }>,
        immediate = false
    ): Promise<T> {
        const module = await this.import(definition, immediate);
        if ("default" in module) {
            return module.default;
        }
        throw new Error(
            `Module '${definition.name}' does not have a default export.`
        );
    }
}
